import { Maybe } from "@figurepos/fgr-lib-business-logic/lib/common/types"
import { flatten } from "lodash"
import { ofType, StateObservable } from "redux-observable"
import { defer, from, Observable, of } from "rxjs"
import { catchError, delayWhen, filter, mergeMap, switchMap, takeUntil } from "rxjs/operators"
import {
    SAVE_ACCOUNT_IMAGE_REQUESTED,
    saveAccountImageFailure,
    saveAccountImageRequested,
    saveAccountImagesSuccess,
} from "../../../api/actions"
import { UpdateSettingsRequestedPayload, UpdateSettingType } from "../../../events/Setting/types"
import { EventType } from "../../../events/types"
import { createEventRequest, requestEvent } from "../../../events/utils"
import {
    EventDataResponse,
    HooSettingsSaveRequest,
    PricingSaveRequestData,
    PricingType,
    SettingsSaveRequest,
    TerminalType,
} from "../../../graphql/types"
import { Router } from "../../../server/router"
import { constructAuditData } from "../auditLog/utils/utils-payload-data"
import { cookieNames, getCookie } from "../../../utils/cookies"
import { logger } from "../../../utils/loggerWrap"
import { ACCOUNT_SELECTED, AGENDA_MOUNTED, REFETCH_DATA } from "../../auth/actions"
import { getHttpHeaders, getSelectedAccountId, getTerminalAuthDataById } from "../../auth/selectors"
import type { TerminalAuthData } from "../../auth/types"
import { getMibSelectedServiceData, getMibServicePickerSelectedId } from "../../common/terminalPickers/selectors"
import { accountImageDeleted, createActionLoadingError, SETTINGS_UPDATED } from "../../data/actions"
import { getSettings } from "../../data/selectors"
import { transformVersions } from "../../data/utils"
import { SET_FILTERS } from "../../filters/actions"
import { getSelectedLocationIdsFromGlobalFilter } from "../../filters/selectors"
import { LOAD_AUTH_LOCATIONS_SUCCESS } from "../../layouts/header/actions"
import { SEND_OPERATIONS_FAILURE, SEND_OPERATIONS_SUCCESS } from "../../operations/actions"
import type { Action, StateObject } from "../../redux/types"
import { MainMenuAgendaEnum } from "../../router/types"
import type { EpicDependencies } from "../../rxjs/configureEpics"
import { NotificationType, openNotification } from "../../uiKit/Notification/Notification"
import { getAuthEmployeeId } from "../employees/employees/selectors"
import { deletePricingClicked, pricingSaveRequested } from "../pricingsAndPromotions/pricing/actions"
import { getPricingById, getPricingsData } from "../pricingsAndPromotions/pricing/selectors"
import { downloadReportFailure } from "../reports/actions"
import {
    waitForAccountSelected,
    waitForGlobalFilterSet,
    waitForLocationsLoaded,
} from "../reports/customers/customersTable/epics"
import {
    ACCOUNT_SETTING_FORM_SUBMITTED,
    BUSINESS_HOMEPAGE_FORM_CLOSED_CLICKED,
    BUSINESS_HOMEPAGE_FORM_SUBMITTED,
    DISCOUNT_CHANGED,
    fetchHooSettingsSuccess,
    FIRST_TIME_DISCOUNT_CHANGED,
    MO_FIRST_ORDER_DISCOUNT_CHANGED,
    moFirstOrderDiscountChanged,
    onSettingsTerminalPickerSubmitted,
    REFETCH_ONLINE_ORDERING_SETTINGS,
    refetchOnlineOrderingSettings,
    SEPARATE_HOO_FORM_SUBMITTED,
    SETTING_FORM_SUBMITTED,
    settingsSaveEnded,
    settingsUpdateEventRequested,
    TERMINAL_SETTING_FORM_SUBMITTED,
    terminalSettingFormSubmitted,
} from "./actions"
import { saveSettingsMutation } from "./deliverySettings/request"
import { fetchHooSettings } from "./mibSettings/request"
import {
    getHooSettingsData,
    getSelectedTerminalIds,
    getSettingByKey,
    getSettingByKeyAndTerminalId,
    getSettingsByKey,
    getTerminalIdsForTerminalPickerInit,
    isMibSettingsAgendaOpened,
} from "./selectors"
import { ChangedSettingLog, SettingsUpdateType } from "./types"
import {
    didSettingValueChange,
    fixNullStringValue,
    getLogChangedSetting,
    resolveSelectedTerminalIdsCookies,
} from "./utils"

export const settingFormSubmittedEpics = (
    actions$: Observable<Action>,
    state$: StateObservable<StateObject>,
    deps: EpicDependencies
) =>
    actions$.pipe(
        ofType(SETTING_FORM_SUBMITTED),
        switchMap((action: Action) => {
            logger.info("settingFormSubmittedEpics - form submitted", action.payload)

            const selectedLocationsIds = action.payload.locationId
                ? [action.payload.locationId]
                : getSelectedLocationIdsFromGlobalFilter(deps.getState())
            const oldVal = action.payload.oldValue
            const newVal = action.payload.newValue
            const arrayOfKeys = Object.keys(oldVal)
            const changedSettingsLog: ChangedSettingLog[] = []

            const settingsToSave: SettingsUpdateType[] = flatten(
                arrayOfKeys
                    .map(key => {
                        const allSettingz = getSettingsByKey(deps.getState(), key)
                        return flatten(
                            selectedLocationsIds.map(locationId => {
                                const findSetting = allSettingz.find(s => s.key === key && s.location_id === locationId)

                                if (!didSettingValueChange(oldVal, newVal, key)) {
                                    return []
                                }
                                changedSettingsLog.push(getLogChangedSetting(oldVal, newVal, key))

                                let value = typeof newVal[key] === "string" ? newVal[key] : JSON.stringify(newVal[key])
                                if (value === undefined) {
                                    return []
                                }

                                value = fixNullStringValue(value)

                                return findSetting
                                    ? {
                                          id: findSetting.id,
                                          key: key,
                                          value: value,
                                          location_id: locationId,
                                          terminal_id: null,
                                      }
                                    : {
                                          id: deps.idgen.next(),
                                          key: key,
                                          value: value,
                                          location_id: locationId,
                                          terminal_id: null,
                                      }
                            })
                        )
                    })
                    .filter(a => a)
            ).filter(a => a)

            logger.info("settingFormSubmittedEpics - changed settings", {
                url: window.location.href,
                locationIds: selectedLocationsIds,
                changes: changedSettingsLog.length > 0 ? changedSettingsLog : "No changes",
            })

            if (settingsToSave.length === 0) {
                return [settingsSaveEnded()]
            }

            return [settingsUpdateEventRequested(settingsToSave)]
        })
    )

export const accountSettingFormSubmittedEpics = (
    actions$: Observable<Action>,
    state$: StateObservable<StateObject>,
    deps: EpicDependencies
) =>
    actions$.pipe(
        ofType(ACCOUNT_SETTING_FORM_SUBMITTED),
        switchMap((action: Action) => {
            logger.info("accountSettingFormSubmittedEpics - form submitted", action.payload)

            const oldVal = action.payload.oldValue
            const newVal = action.payload.newValue
            const arrayOfKeys = Object.keys(oldVal)

            const changedSettingsLog: ChangedSettingLog[] = []
            const settingToSave: SettingsUpdateType[] = flatten(
                arrayOfKeys
                    .map(key => {
                        if (!didSettingValueChange(oldVal, newVal, key)) {
                            return []
                        }
                        changedSettingsLog.push(getLogChangedSetting(oldVal, newVal, key))

                        const findSetting = getSettingByKey(deps.getState(), key)
                        let value = typeof newVal[key] === "string" ? newVal[key] : JSON.stringify(newVal[key])
                        value = fixNullStringValue(value)
                        return findSetting
                            ? {
                                  id: findSetting.id,
                                  key: key,
                                  value: value,
                                  location_id: null,
                                  terminal_id: null,
                              }
                            : {
                                  key: key,
                                  value: value,
                                  location_id: null,
                                  terminal_id: null,
                              }
                    })
                    .filter(a => a)
            ).filter(a => a)

            logger.info("accountSettingFormSubmittedEpics - changed settings", {
                url: window.location.href,
                changes: changedSettingsLog.length > 0 ? changedSettingsLog : "No changes",
            })

            if (settingToSave.length === 0) {
                return [settingsSaveEnded()]
            }

            return [settingsUpdateEventRequested(settingToSave)]
        })
    )

export const terminalSettingFormSubmittedEpics = (
    actions$: Observable<Action>,
    state$: StateObservable<StateObject>,
    deps: EpicDependencies
) =>
    actions$.pipe(
        ofType(TERMINAL_SETTING_FORM_SUBMITTED),
        switchMap((action: Action) => {
            logger.info("terminalSettingFormSubmittedEpics - form submitted", action.payload)
            let selectedTerminalIds: string[] = []

            if (action.payload.terminalType === TerminalType.Mib) {
                const mibSelectedId = getMibServicePickerSelectedId(deps.getState())
                selectedTerminalIds = mibSelectedId ? [mibSelectedId] : []
            } else {
                selectedTerminalIds = getSelectedTerminalIds(deps.getState())
            }

            const oldVal = action.payload.oldValue
            const newVal = action.payload.newValue
            const arrayOfKeys = Object.keys(oldVal)

            const changedSettingsLog: ChangedSettingLog[] = []
            const settingsToSave = flatten(
                arrayOfKeys
                    .map(key => {
                        const allSettingz = getSettingsByKey(deps.getState(), key)
                        return flatten(
                            selectedTerminalIds.map(terminalId => {
                                const terminal: Maybe<TerminalAuthData> = getTerminalAuthDataById(
                                    deps.getState(),
                                    terminalId
                                )

                                if (!terminal) {
                                    return []
                                }

                                if (!didSettingValueChange(oldVal, newVal, key)) {
                                    return []
                                }
                                changedSettingsLog.push(getLogChangedSetting(oldVal, newVal, key))

                                const findSetting = allSettingz.find(s => s.key === key && s.terminal_id === terminalId)
                                let value = typeof newVal[key] === "string" ? newVal[key] : JSON.stringify(newVal[key])

                                // Field was not touched
                                if (value === undefined) {
                                    return []
                                }

                                value = fixNullStringValue(value)
                                return findSetting
                                    ? {
                                          id: findSetting.id,
                                          key: key,
                                          value: value,
                                          location_id: terminal.locationId,
                                          terminal_id: terminalId,
                                      }
                                    : {
                                          id: deps.idgen.next(),
                                          key: key,
                                          value: value,
                                          location_id: terminal.locationId,
                                          terminal_id: terminalId,
                                      }
                            })
                        )
                    })
                    .filter(a => a)
            ).filter(a => a)

            logger.info("terminalSettingFormSubmittedEpics - changed settings", {
                url: window.location.href,
                terminalIds: selectedTerminalIds,
                changes: changedSettingsLog.length > 0 ? changedSettingsLog : "No changes",
            })

            if (settingsToSave.length === 0) {
                return [settingsSaveEnded()]
            }

            return [settingsUpdateEventRequested(settingsToSave)]
        })
    )

export const discountChangedEpic = (
    actions$: Observable<Action>,
    state$: StateObservable<StateObject>,
    deps: EpicDependencies
) =>
    actions$.pipe(
        ofType(DISCOUNT_CHANGED),
        switchMap((action: Action) => {
            const pricings = getPricingsData(deps.getState())
            const lastSortIndex = pricings && pricings.data ? pricings.data.length : 0
            let pricingSettingId: any = null
            const selectedTerminalId = getMibServicePickerSelectedId(deps.getState())

            if (!selectedTerminalId) {
                logger.warn("discountChangedEpic - no selectedTerminalIds found")
                return []
            }

            pricingSettingId = getSettingByKeyAndTerminalId(
                deps.getState(),
                "MIB_PROMO_CODE_SP_ID",
                null,
                selectedTerminalId
            )
            const specialPricing =
                pricingSettingId && pricingSettingId.value
                    ? getPricingById(deps.getState(), pricingSettingId.value)
                    : null
            const pricingId = specialPricing ? specialPricing.id : deps.idgen.next()
            let pricingAction: any = null
            const auditData = constructAuditData(deps, false)

            logger.info("discountChangedEpic - discount saved", {
                turnedOn: action.payload.turnedOn,
                value: action.payload.value,
                pricingSettingId: pricingSettingId ? pricingSettingId.id : null,
                pricingId: pricingId,
                existingSpecialPricing: specialPricing,
            })
            if (action.payload.turnedOn && action.payload.value) {
                if (specialPricing) {
                    pricingAction = pricingSaveRequested({
                        ...specialPricing,
                        autoApplyHours: null,
                        sortIndex: specialPricing.sortIndex || 0,
                        percentage: action.payload.value * 1000,
                        isCreating: false,
                        auditData: auditData,
                        pricingType: PricingType.PromoCodeDiscount,
                    })
                } else {
                    const newPricingData: PricingSaveRequestData = {
                        id: pricingId,
                        name: "Promo Discount",
                        percentage: action.payload.value * 1000,
                        absolute: null,
                        isOrderLevel: true,
                        isItemLevel: false,
                        isActive: true,
                        isAutoApply: false,
                        sortIndex: lastSortIndex + 1,
                        autoApplyHours: null,
                        isCreating: true,
                        auditData: auditData,
                        pricingType: PricingType.PromoCodeDiscount,
                    }

                    pricingAction = pricingSaveRequested(newPricingData)
                }
            } else if (specialPricing) {
                // Check if there is last setting, if yeah, remove
                const allPricingIdSettings = getSettingsByKey(deps.getState(), "MIB_PROMO_CODE_SP_ID")
                const allPricingIdActive = allPricingIdSettings.filter(s => s.value !== null && s.value !== "null")

                if (
                    allPricingIdSettings.length === 1 ||
                    allPricingIdActive.length === 1 ||
                    allPricingIdSettings.length === allPricingIdActive.length
                ) {
                    pricingAction = deletePricingClicked(specialPricing.id, PricingType.PromoCodeDiscount)
                }
            }

            return [
                pricingAction,
                terminalSettingFormSubmitted(
                    {
                        MIB_PROMO_CODE_SP_ID: pricingSettingId,
                    },
                    {
                        MIB_PROMO_CODE_SP_ID:
                            action.payload.turnedOn && action.payload.value != null && action.payload.value !== "null"
                                ? pricingId
                                : null,
                    },
                    TerminalType.Mib
                ),
            ].filter(a => a)
        })
    )

export const firstTimeDiscountChangedEpic = (
    actions$: Observable<Action>,
    state$: StateObservable<StateObject>,
    deps: EpicDependencies
) =>
    actions$.pipe(
        ofType(FIRST_TIME_DISCOUNT_CHANGED),
        switchMap((action: Action) => {
            const pricings = getPricingsData(deps.getState())
            const lastSortIndex = pricings && pricings.data ? pricings.data.length : 0
            let pricingSettingId: any = null
            const selectedTerminalId = getMibServicePickerSelectedId(deps.getState())

            if (!selectedTerminalId) {
                logger.warn("firstTimeDiscountChangedEpic - no selectedTerminalIds found")
                return []
            }

            pricingSettingId = getSettingByKeyAndTerminalId(
                deps.getState(),
                "MIB_FIRST_TIME_PROMO_DISCOUNT_SP_ID",
                null,
                selectedTerminalId
            )
            const specialPricing =
                pricingSettingId && pricingSettingId.value
                    ? getPricingById(deps.getState(), pricingSettingId.value)
                    : null
            const pricingId = specialPricing ? specialPricing.id : deps.idgen.next()
            let pricingAction: any = null
            const auditData = constructAuditData(deps, false)

            logger.info("firstTimeDiscountChangedEpic - discount saved", {
                turnedOn: action.payload.turnedOn,
                value: action.payload.value,
                pricingSettingId: pricingSettingId ? pricingSettingId.id : null,
                pricingId: pricingId,
                existingSpecialPricing: specialPricing,
            })

            if (action.payload.turnedOn && action.payload.value) {
                if (specialPricing) {
                    pricingAction = pricingSaveRequested({
                        ...specialPricing,
                        autoApplyHours: null,
                        sortIndex: specialPricing.sortIndex || 0,
                        percentage: action.payload.value * 1000,
                        isCreating: false,
                        auditData: auditData,
                        pricingType: PricingType.FirstTimePromoDiscount,
                    })
                } else {
                    const newPricingData: PricingSaveRequestData = {
                        id: pricingId,
                        name: "First Time Promo Discount",
                        percentage: action.payload.value * 1000,
                        absolute: null,
                        isOrderLevel: true,
                        isItemLevel: false,
                        isActive: true,
                        isAutoApply: false,
                        sortIndex: lastSortIndex + 1,
                        autoApplyHours: null,
                        isCreating: true,
                        auditData: auditData,
                        pricingType: PricingType.FirstTimePromoDiscount,
                    }

                    pricingAction = pricingSaveRequested(newPricingData)
                }
            } else if (specialPricing) {
                // Check if there is last setting, if yeah, remove
                const allPricingIdSettings = getSettingsByKey(deps.getState(), "MIB_FIRST_TIME_PROMO_DISCOUNT_SP_ID")
                const allPricingIdActive = allPricingIdSettings.filter(s => s.value !== null && s.value !== "null")

                if (
                    allPricingIdSettings.length === 1 ||
                    allPricingIdActive.length === 1 ||
                    allPricingIdSettings.length === allPricingIdActive.length
                ) {
                    pricingAction = deletePricingClicked(specialPricing.id, PricingType.FirstTimePromoDiscount)
                }
            }

            return [
                pricingAction,
                terminalSettingFormSubmitted(
                    {
                        MIB_FIRST_TIME_PROMO_DISCOUNT_SP_ID: pricingSettingId,
                    },
                    {
                        MIB_FIRST_TIME_PROMO_DISCOUNT_SP_ID:
                            action.payload.turnedOn && action.payload.value != null && action.payload.value !== "null"
                                ? pricingId
                                : null,
                    },
                    TerminalType.Mib
                ),
            ].filter(a => a)
        })
    )

export const moFirstOrderDiscountChangedEpic = (
    actions$: Observable<Action>,
    state$: StateObservable<StateObject>,
    deps: EpicDependencies
) =>
    actions$.pipe(
        ofType(MO_FIRST_ORDER_DISCOUNT_CHANGED),
        switchMap((action: ReturnType<typeof moFirstOrderDiscountChanged>) => {
            const state = deps.getState()
            const terminalId = getMibServicePickerSelectedId(state)
            if (!terminalId) {
                return []
            }

            const key = "MO_FIRST_ORDER_PROMO_DISCOUNT_SP_ID"
            const setting = getSettingByKeyAndTerminalId(state, key, null, terminalId)
            const pricing = getPricingById(state, setting?.value)
            const { turnedOn, type, value, name } = action.payload
            const settingBefore = { [key]: { value: setting?.value ?? null, indeterminate: false } }
            if (!turnedOn) {
                return [terminalSettingFormSubmitted(settingBefore, { [key]: null }, TerminalType.Mib)]
            }

            const promoName = name?.trim() || "First App Order"
            const amount = value == null ? 0 : Math.round(value)
            if (!Number.isFinite(amount) || amount <= 0) {
                return []
            }
            if (type === "percentage" && amount > 100000) {
                return []
            }

            const shared = pricing
                ? getSettings(state).some(s => s.value === pricing.id && s.id !== setting?.id)
                : false
            const existingId = shared ? null : pricing?.id
            const id = existingId ?? deps.idgen.next()
            return [
                pricingSaveRequested({
                    id: id,
                    name: promoName,
                    percentage: type === "percentage" ? -amount : null,
                    absolute: type === "absolute" ? -amount : null,
                    isOrderLevel: true,
                    isItemLevel: false,
                    isActive: true,
                    isAutoApply: false,
                    sortIndex: pricing?.sortIndex ?? (getPricingsData(state)?.data.length ?? 0) + 1,
                    autoApplyHours: null,
                    isCreating: !pricing || shared,
                    auditData: constructAuditData(deps, false),
                    pricingType: PricingType.MoFirstOrderPromoDiscount,
                }),
                terminalSettingFormSubmitted(settingBefore, { [key]: id }, TerminalType.Mib),
            ]
        })
    )

export const businessAppearanceFormClosedEpics = (actions$: Observable<Action>) =>
    actions$.pipe(
        ofType(BUSINESS_HOMEPAGE_FORM_CLOSED_CLICKED, BUSINESS_HOMEPAGE_FORM_SUBMITTED),
        switchMap(() => {
            Router.push("/make-it-butter-settings")
            return []
        })
    )

export const businessAppearanceFormSubmittedEpics = (actions$: Observable<Action>) =>
    actions$.pipe(
        ofType(BUSINESS_HOMEPAGE_FORM_SUBMITTED),
        switchMap((action: Action) => {
            logger.info("businessAppearanceFormSubmittedEpics - form submitted", {
                accountImageIdsToDelete: action.payload.accountImageIdsToDelete,
                logo: action.payload.logo,
                backgroundImage: action.payload.backgroundImage,
            })

            const deleteActions = action.payload.accountImageIdsToDelete.map(id => accountImageDeleted(id))

            return [
                ...deleteActions,
                action.payload.logo
                    ? saveAccountImageRequested(action.payload.logo, "mib-business-homepage-logo")
                    : null,
                action.payload.backgroundImage
                    ? saveAccountImageRequested(
                          action.payload.backgroundImage,
                          "mib-business-homepage-background-desktop"
                      )
                    : null,
            ].filter(a => a)
        })
    )

export const saveAccountImageRequestedEpic = (
    actions$: Observable<Action>,
    state$: StateObservable<StateObject>,
    deps: EpicDependencies
) =>
    actions$.pipe(
        ofType(SAVE_ACCOUNT_IMAGE_REQUESTED),
        mergeMap((action: Action) => {
            logger.info("saveAccountImageRequestedEpic - form submitted", {
                type: action.payload.type,
                terminalId: action.payload.terminalId,
                id: action.payload.id,
                sortIndex: action.payload.sortIndex,
                label: action.payload.label,
            })

            const accountId = getSelectedAccountId(deps.getState())

            if (!accountId) {
                logger.warn("saveAccountImageRequestedEpic - no accountId found")
                return []
            }

            return deps.api
                .saveAccountImageApi(
                    getHttpHeaders(deps.getState()),
                    accountId,
                    action.payload.file,
                    action.payload.type,
                    action.payload.terminalId,
                    action.payload.id,
                    action.payload.sortIndex,
                    action.payload.label
                )
                .pipe(
                    switchMap(res => {
                        // @ts-expect-error
                        if (res && res.message === "ok") {
                            return [
                                saveAccountImagesSuccess(
                                    // @ts-expect-error
                                    res.account_images
                                        .filter(ai => !ai.deleted)
                                        .map(ai => ({
                                            id: ai.id,
                                            locationId: ai.locationId,
                                            terminalId: ai.terminalId,
                                            label: ai.label || null,
                                            sortIndex: ai.sortIndex,
                                            path: ai.path,
                                            type: ai.type,
                                            awsBased: true,
                                        })),
                                    transformVersions(
                                        // @ts-expect-error
                                        res.account_images.map(obj => ({
                                            id: obj.id,
                                            version: obj.version,
                                        }))
                                    )
                                ),
                            ]
                        }
                        // @ts-expect-error
                        return [saveAccountImageFailure(res ? res.message : null)]
                    }),
                    catchError(error => from([saveAccountImageFailure(error)]))
                )
        })
    )

export const showSavingLoadingEpics = (actions$: Observable<Action>) =>
    actions$.pipe(
        ofType(SETTING_FORM_SUBMITTED, ACCOUNT_SETTING_FORM_SUBMITTED, TERMINAL_SETTING_FORM_SUBMITTED),
        delayWhen(() => actions$.pipe(ofType(SEND_OPERATIONS_SUCCESS))),
        takeUntil(actions$.pipe(ofType(SEND_OPERATIONS_FAILURE))),
        switchMap(() => {
            return [settingsSaveEnded()]
        })
    )

export const initializeTerminalPickerEpics = (
    actions$: Observable<Action>,
    state$: StateObservable<StateObject>,
    deps: EpicDependencies
) =>
    actions$.pipe(
        ofType(LOAD_AUTH_LOCATIONS_SUCCESS),
        switchMap((action: Action) => {
            const authTerminalIds = flatten(action.payload.locations.map(l => l.terminals)).map(t => t.id)
            const filteredTerminalIds: string[] = getTerminalIdsForTerminalPickerInit(deps.getState(), authTerminalIds)
            const terminalIdsFromCookiesRaw = getCookie(cookieNames.terminalIds)
            const terminalIdsFromCookies = terminalIdsFromCookiesRaw ? JSON.parse(terminalIdsFromCookiesRaw) : null
            const terminalIds = resolveSelectedTerminalIdsCookies(filteredTerminalIds, terminalIdsFromCookies)
            return [onSettingsTerminalPickerSubmitted(terminalIds)]
        })
    )

export const onlineOrderingTerminalSelectedEpics = (actions$: Observable<Action>) =>
    actions$.pipe(
        ofType(SET_FILTERS),
        filter((action: Action) => action.payload.filters && action.payload.filters.selectedMibTerminalId),
        switchMap(() => {
            return [refetchOnlineOrderingSettings()]
        })
    )
// Fetching new settings from GraphQL, no support for multiple terminalIds

export const fetchOnlineOrderingDataEpics = (
    actions$: Observable<Action>,
    state$: StateObservable<StateObject>,
    deps: EpicDependencies
) =>
    actions$.pipe(
        ofType(AGENDA_MOUNTED, ACCOUNT_SELECTED, REFETCH_DATA, REFETCH_ONLINE_ORDERING_SETTINGS),
        filter((action: Action) =>
            action.type === AGENDA_MOUNTED
                ? action.payload.type === MainMenuAgendaEnum.MakeItButterSettings
                : isMibSettingsAgendaOpened(deps.getState())
        ),
        delayWhen(action => waitForGlobalFilterSet(actions$, deps.getState(), action)),
        delayWhen(action => waitForAccountSelected(actions$, deps.getState(), action)),
        delayWhen(action => waitForLocationsLoaded(actions$, deps.getState(), action)),
        switchMap(() => {
            const serviceId = getMibServicePickerSelectedId(deps.getState())
            if (!serviceId) {
                logger.info("fetchOnlineOrderingDataEpics - no service selected")
                return []
            }

            const service = getTerminalAuthDataById(deps.getState(), serviceId)
            if (!service || !service.locationId) {
                logger.info("fetchOnlineOrderingDataEpics - no service data")
                return []
            }

            return defer(() => fetchHooSettings(getHttpHeaders(deps.getState()), service.locationId, service.id)).pipe(
                switchMap(response => {
                    if (response) {
                        return [fetchHooSettingsSuccess(response)]
                    }

                    return []
                }),
                catchError(error => of(downloadReportFailure(error)))
            )
        })
    )

export const separateHooFormSubmittedEpics = (
    actions$: Observable<Action>,
    state$: StateObservable<StateObject>,
    deps: EpicDependencies
) =>
    actions$.pipe(
        ofType(SEPARATE_HOO_FORM_SUBMITTED),
        switchMap((action: Action) => {
            const service = getMibSelectedServiceData(deps.getState())

            if (!service || !service.locationId) {
                logger.info("separateHooFormSubmittedEpics - no service found")
                return []
            }

            const prevHooSettings = getHooSettingsData(deps.getState())
            logger.info("separateHooFormSubmittedEpics - form submitted", {
                data: action.payload.data,
                previousData: prevHooSettings,
                service: service,
            })

            const data: HooSettingsSaveRequest = action.payload.data
            const dataToSave: SettingsSaveRequest = {
                locationId: service.locationId,
                terminalId: service.id,
                hooSetttings: data,
                auditData: constructAuditData(deps, false),
            }
            return defer(() => saveSettingsMutation(getHttpHeaders(deps.getState()), dataToSave)).pipe(
                switchMap(() => {
                    return []
                }),
                catchError(error => of(downloadReportFailure(error)))
            )
        })
    )

export const settingsUpdateEventRequestedEpic = (
    actions$: Observable<Action>,
    state$: StateObservable<StateObject>,
    deps: EpicDependencies
) =>
    actions$.pipe(
        ofType(SETTINGS_UPDATED),
        switchMap((action: Action) => {
            const settingsToSave: SettingsUpdateType[] = action.payload.settings
            const accountId = getSelectedAccountId(deps.getState())
            const authEmployeeId = getAuthEmployeeId(deps.getState())

            const eventPayload: UpdateSettingType[] = settingsToSave.map(s => ({
                key: s.key,
                value: s.value,
                locationId: s.location_id,
                terminalId: s.terminal_id,
            }))

            const auditPayload: UpdateSettingsRequestedPayload = {
                settings: eventPayload,
                auditData: {
                    ip: null, // IP added on manage-api
                    webappPath: window.location.pathname,
                    editorUserAccountId: authEmployeeId ?? null,
                },
            }

            return defer(() =>
                requestEvent(
                    getHttpHeaders(deps.getState()),
                    createEventRequest(
                        EventType.SettingsUpdateRequested,
                        {
                            correlationId: deps.idgen.next(),
                            accountId: accountId as string,
                        },
                        auditPayload,
                        0
                    )
                )
            ).pipe(
                switchMap((res: EventDataResponse) => {
                    const id = res?.id
                    if (!id) {
                        throw new Error("settingsUpdateEventRequestedEpic - Event ID is missing")
                    }

                    logger.info("settingsUpdateEventRequestedEpic - event succeeded", { eventId: id })
                    openNotification(NotificationType.Success, "Settings saved", "Settings saved successfully")

                    return [settingsSaveEnded()]
                }),
                catchError(err => {
                    logger.error("settingsUpdateEventRequestedEpic - event failed", err)
                    openNotification(NotificationType.Error, "Failed to save settings", err.message)
                    return of(createActionLoadingError(err), settingsSaveEnded())
                })
            )
        })
    )

export default [
    saveAccountImageRequestedEpic,
    businessAppearanceFormSubmittedEpics,
    businessAppearanceFormClosedEpics,
    discountChangedEpic,
    firstTimeDiscountChangedEpic,
    moFirstOrderDiscountChangedEpic,
    showSavingLoadingEpics,
    settingFormSubmittedEpics,
    accountSettingFormSubmittedEpics,
    terminalSettingFormSubmittedEpics,
    initializeTerminalPickerEpics,
    fetchOnlineOrderingDataEpics,
    separateHooFormSubmittedEpics,
    onlineOrderingTerminalSelectedEpics,
    settingsUpdateEventRequestedEpic,
]
